<template>
  <div class="board-members-manager">
    <add-board-member
      :board="board"
      :members="membersForModal"
      @updated="() => $emit('updated')"
    />

    <b-overlay :show="isProcessing">
      <b-table
        :fields="fields"
        :items="boardMembers"
        sticky-header
        style="max-height: 540px"
        responsive
        show-empty
      >
        <template #cell(name)="row">
          <div class="d-flex justify-content-between">
            <div class="d-flex align-items-center">
              <b-avatar
                :src="getStorage(row.item.avatar)"
                :text="getInitials(row.item.name)"
                badge
                :badge-variant="row.item.isOnline ? 'success' : 'secondary'"
              />
              <div class="ml-2">
                <div class="font-weight-bold">
                  {{ row.item.name }}
                </div>
                <div class="text-muted small">
                  {{ row.item.email }}
                </div>
              </div>
            </div>

            <b-dropdown
              variant="link"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="14"
                />
              </template>

              <b-dropdown-item
                :disabled="isProcessing"
                @click="$router.push({
                  name: 'project-board-member-report',
                  params: { boardUid: base58Converter(board.uuid) },
                  query: { memberUid: base58Converter(row.item.uuid) },
                })"
              >
                <feather-icon
                  icon="BarChartIcon"
                  size="14"
                />
                <span class="ml-1">{{ $t('Performance Report') }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                variant="danger"
                :disabled="isProcessing"
                @click="confirmRemove(row.item)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
                <span class="ml-1">{{ $t('Remove') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
    </b-overlay>

    <b-modal
      id="board-remove-member-modal"
      :title="selectedUser ? `Remove ${selectedUser.name} from board` : 'Remove user'"
      ok-title="Remove"
      ok-variant="danger"
      @ok="removeMember"
    >
      <span v-if="selectedUser">Are you sure you want to remove {{ selectedUser.name }} from this board?</span>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable,
  BDropdown,
  BDropdownItem,
  BOverlay,
  BModal,
  BAvatar,
  VBTooltip,
} from 'bootstrap-vue'
import boardsApi from '@/plugins/graphql/boards'
import { mapActions, mapGetters } from 'vuex'
import AddBoardMember from './AddBoardMember.vue'

export default {
  name: 'BoardMembersManager',
  components: {
    BTable,
    BDropdown,
    BDropdownItem,
    BOverlay,
    BModal,
    BAvatar,
    AddBoardMember,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    board: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      searchFor: '',
      isProcessing: false,
      selectedUser: null,
    }
  },
  computed: {
    ...mapGetters('project', ['GET_CURRENT_PROJECT']),
    fields() {
      return [
        { label: this.$t('Board Member'), key: 'name', thClass: 'text-capitalize w-75' },
      ]
    },
    boardMembers() {
      return this.board.members?.data || []
    },
    membersForModal() {
      return this.boardMembers.map(m => ({ uuid: m.uuid }))
    },
  },
  methods: {
    ...mapActions('boards', ['removeMember']),
    confirmRemove(user) {
      this.selectedUser = user
      this.$bvModal.show('board-remove-member-modal')
    },
    async removeMember() {
      if (!this.selectedUser) return

      this.isProcessing = true

      try {
        const memberUid = this.selectedUser.uuid
        // Use removeBoardMember mutation
        const response = await boardsApi.removeBoardMember({
          projectUid: this.GET_CURRENT_PROJECT.uuid,
          boardUid: this.board.uuid,
          memberUid,
        })

        this.showSuccess(response.data.removeBoardMember.message)
        this.$emit('updated')
      } catch (err) {
        this.showError(err.message || this.$t('Failed to remove member'))
      } finally {
        this.selectedUser = null
        this.$bvModal.hide('board-remove-member-modal')
        setTimeout(() => {
          this.isProcessing = false
        }, 1000)
      }
    },
  },
}
</script>

<style scoped>
.btn-icon-sm {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
